import moment from "moment";
import { useMemo } from "react";
import { DeepPartial } from "react-hook-form";
import { useFetchable } from "../../../store/common/fetchable";
import { isFetchableSuccess } from "../../../store/common/fetchable/helpers";
import { LaunchConfigurationsDTO } from "../../../store/launch-configurations/models/dto";
import { Users } from "../../../store/users/reducer";

type launchType = DeepPartial<LaunchConfigurationsDTO.Requests.SetSingle.T>;

export const useNewLaunchConfigDefaults: () => launchType = () => {
  const currentUser = useFetchable(Users.current);

  return useMemo(
    () => ({
      configuration: {
        between: {
          start: new Date(),
          end: moment().add(1, "week").toDate(),
        },
        supportedFormLanguages: ["en_GB"],
        referrer:
          (isFetchableSuccess(currentUser) &&
            currentUser.data.companyDetails?.name) ||
          "noreferrer",
        css: {
          headerBackgroundColor: {
            degree: 5,
            start: "#062A7F",
            startAlpha: 0,
            end: "#4294bd",
            endAlpha: 100,
          },
          headerTextColor: "#ffffff",
          formBackgroundColor: "#e6e6e6",
          textColor: "#000000",
          buttonPrimaryColor: "#22719e",
          buttonSecondaryColor: "#ffffff",
        },
        allowMultipleSubmissions: true,
        header: {
          imageUrl: null,
          text: {},
        },
        merchantConfig: {
          createUserProfile: true,
          performOtp: true,
          preVerifiedSourceWallet: false,
        },
        poaRequiredCountries: ["ALL"],
        prohibitedCountries: [
          "AL",
          "BY",
          // "BU",
          "MM",
          "US",
          "KP",
          "IR",
          "MK",
          "MD",
          "SS",
          "CF",
          "YE",
          "VE",
          "LY",
          "SY",
          "LB",
          "IQ",
          "SO",
          "CU",
          "BA",
          "CI",
          "ZW",
          "RS",
          "XK",
          "LR",
          "CG",
          "CD",
          "CN",
          "UM",
        ],
        welcomeMessage: {
          en_GB:
            "Kindly fill in the details as requested to ensure a higher approval and accuracy probability.",
        },
      },
      pages: [
        {
          title: {
            en_GB: "Initial Details",
          },
          fields: [
            {
              field: "user.emailAddress",
              title: {},
              desc: {},
              optional: false,
            },
            {
              field: "user.applicationPin",
              title: {},
              desc: {},
              optional: false,
            },
          ],
        },
        {
          title: {
            en_GB: "Personal Information",
          },
          fields: [
            {
              field: "user.names",
              title: {},
              desc: {},
              optional: false,
            },
            {
              field: "user.dateOfBirth",
              title: {},
              desc: {},
              optional: false,
            },
            {
              field: "user.nationality",
              title: {},
              desc: {},
              optional: false,
            },
            {
              field: "user.countryOfResidence",
              title: {},
              desc: {},
              optional: false,
            },
            {
              field: "user.physicallyLocatedIn",
              title: {},
              desc: {},
              optional: false,
            },
          ],
        },
        {
          title: {
            en_GB: "Wallet Information",
          },
          fields: [
            {
              field: "sourceWallet",
              title: {
                en_GB: "Please enter the ETH address.",
              },
              desc: {
                en_GB:
                  "Should your application be successful and you are eligible, funds must be sent from this address.",
              },
              optional: false,
              type: "DROP_DOWN",
              typeDetails: {
                availableOptions: {
                  en_GB: ["ETH"],
                },
              },
            },
            {
              field: "destinationWallet",
              title: {
                en_GB: "Please enter the ETH address.",
              },
              desc: {
                en_GB:
                  "Should your application be successful and you are eligible, funds must be sent from this address.",
              },
              optional: false,
              type: "DROP_DOWN",
              typeDetails: {
                availableOptions: {
                  en_GB: ["ETH"],
                },
              },
            },
            {
              field: "pledge",
              title: {
                en_GB: "Pledge",
              },
              desc: {
                en_GB: "Enter the pledged amount",
              },
              optional: false,
            },
          ],
        },
        {
          title: {
            en_GB: "Personal Information Continued",
          },
          fields: [
            {
              field: "user.address",
              title: {},
              desc: {},
              optional: false,
            },
          ],
        },
        {
          title: {
            en_GB: "Proof Of Identity",
          },
          fields: [
            {
              field: "identityDocument",
              title: {},
              desc: {},
              optional: false,
              type: "DROP_DOWN",
              typeDetails: {
                availableOptions: {
                  en_GB: ["DRIVING_LICENSE", "ID_CARD", "PASSPORT"],
                },
              },
            },
          ],
        },
        {
          title: {
            en_GB: "Face Authentication",
          },
          fields: [
            {
              field: "faceAttachment",
              title: {},
              desc: {},
              optional: false,
            },
          ],
        },
        {
          title: {
            en_GB: "Proof Of Address",
          },
          fields: [
            {
              field: "addressAttachment",
              title: {},
              desc: {},
              optional: false,
              type: "DROP_DOWN",
              typeDetails: {
                availableOptions: {
                  en_GB: [
                    "EMPLOYER_LETTER",
                    "TAX_BILL",
                    "UTILITY_BILL",
                    "RENT_AGREEMENT",
                    "BANK_STATEMENT",
                  ],
                },
              },
            },
          ],
        },
        {
          title: {
            en_GB: "Declarations",
          },
          fields: [
            {
              field: "regulations.pep",
              title: {},
              desc: {},
              optional: false,
            },
            {
              field: "policies.declarationStatements",
              title: {},
              desc: {
                en_GB:
                  "<ol><li>I have never been adjudged bankrupt</li><li>I have never been subject to any investigation by a governmental, professional or other regulatory or statutory body</li><li>I have never been a director, shareholder or manager of a business entity which has been the subject of any investigation as aforesaid, or which has been adjudged bankrupt, compulsorily wound up or has made any compromise or arrangement with its creditors</li><li>I have never been convicted of any criminal offence in any jurisdiction (other than a minor offence in connection with the use of a motor vehicle) and no criminal proceedings were instituted and/or are pending against me in any jurisdiction</li><li>None of the funds (USDT, USDC) that I will contribute to the Company further to the Public Token Offer has been derived from, or related to, any activity that is deemed criminal under the laws of any applicable jurisdiction</li><li>No contribution made by the Investor to the Company and no allocation of Tokens to the Investor from the Company shall cause the Company to be in violation of The Criminal Justice (Money Laundering and Terrorist Financing) Act 2010 in Ireland, and the (EU) 2015/849 - Fourth Money Laundering Directive, Regulation (EU) 2015/847 on information accompanying transfers of funds. I declare that the information and/or documents provided during this KYC-AML process is true and correct according to my best knowledge. I hereby fully understand that providing false information is treated as a criminal offence.</li></ol>",
              },
              optional: false,
            },
            {
              field: "policies.sekuritanceStatements",
              title: {},
              desc: {},
              optional: false,
            },
            {
              field: "regulations.resideSanctionedCountry",
              title: {},
              desc: {
                en_GB:
                  "I confirm that I do not reside in any other UN sanctioned countries at this moment",
              },
              optional: false,
            },
            {
              field: "policies.finalDeclaration",
              title: {
                en_GB: "Submission Declaration",
              },
              desc: {
                en_GB:
                  "By submitting this Application Form, the Participant confirms that all of the information provided in this Application Form is complete and true and the Participant has read, reviewed and agrees to SEKURITANCE Terms and Conditions and undertakes to comply with them. Participant assumes full responsibility for the choice of his responses above. By submitting this Application Form, the Participant agrees that SEKURITANCE carries out processing of personal and other data in accordance with the General Data Protection Regulation (GDPR) and other applicable laws and regulations and accept to have a user and a profile in the Sekuritance Identity System. According to GDPR, SEKURITANCE carries out the processing of personal data of the Participant, its representatives, authorised persons, representatives, beneficial owners and other third parties related to the Participant, including the collection, registration, input, storage, arranging, modification, using, transferring, transmission and disclosure of data, blocking or deletion, and the Participant is obliged to obtain consent to all data processing mentioned in this clause from all persons whose personal data they have transferred to SEKURITANCE. The purpose of personal data processing is compliance with local and international laws and regulations, Participant registration, the provision, offering and maintenance of SEKURITANCE products and services, the security of employees, the security of SEKURITANCE property (assets), the performance of the duties prescribed by law, and other legal interests of the SEKURITANCE. The Personal Data Processor is SEKURITANCE. The Participant and SEKURITANCE hereby agree that electronic mail (e-mail) may be used for exchanging information and documents. The Participant represents that he understand the risks inherent to use of e-mail, that SEKURITANCE has informed him about potential risks and explained their consequences, and that the aforementioned information is understood by the Participant.",
              },
              optional: false,
            },
          ],
        },
      ],
    }),
    [currentUser]
  );
};
